import { useEffect, useRef, useState } from 'react';
import { Icons } from '../Icons.jsx';
import { speak, hasCroatianVoice } from '../../utils/speech.js';
import { answersMatch } from '../../utils/normalize.js';
import { BALL } from '../../theme.js';
import { t } from '../../i18n.js';
import { Sentry } from '../../sentry.js';

const T = BALL;

export default function Listen({ exercise, lang, onResult, locked, registerSubmit }) {
  const [value, setValue] = useState('');
  const [showHint, setShowHint] = useState(false);
  const inputRef = useRef(null);
  const isSentence = exercise.mode === 'sentence';

  useEffect(() => {
    setValue('');
    setShowHint(false);
    inputRef.current?.focus();
    const tid = setTimeout(() => speak(exercise.speakText), 200);
    return () => clearTimeout(tid);
  }, [exercise]);

  function submit() {
    if (locked) return;
    const correct = answersMatch(value, exercise.accept);
    Sentry.addBreadcrumb({ category: 'exercise', message: 'listen-submit', data: { correct, mode: exercise.mode } });
    onResult({
      correct,
      given: value,
      expected: Array.isArray(exercise.accept) ? exercise.accept[0] : exercise.accept,
    });
  }

  registerSubmit?.(submit);

  const eyebrow = lang === 'en' ? 'LISTEN AND TYPE' : 'LYSSNA OCH SKRIV';
  const title = lang === 'en' ? 'What did you hear?' : 'Vad hörde du?';
  const subtitle = lang === 'en'
    ? 'Type the Croatian word (not the translation)'
    : 'Skriv det kroatiska ordet (inte översättningen)';
  const tapToHear = lang === 'en' ? 'TAP TO HEAR' : 'TRYCK FÖR ATT HÖRA';
  const placeholderWord = lang === 'en' ? 'Type what you hear…' : 'Skriv det du hör…';

  const inputCommonStyle = {
    width: '100%',
    border: `2px solid ${T.hairline}`,
    borderRadius: 14, padding: '14px 16px',
    fontSize: 18, fontFamily: 'inherit', fontWeight: 600,
    color: T.ink, background: T.bgAlt, outline: 'none',
  };

  return (
    <div style={{ padding: '4px 22px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{
          fontSize: 11, fontWeight: 800, color: T.blue, letterSpacing: '0.12em',
        }}>{eyebrow}</div>
        <h2 style={{
          fontSize: 24, fontWeight: 900, color: T.ink, margin: '6px 0 0', lineHeight: 1.15,
        }}>{title}</h2>
        <div style={{
          fontSize: 13, color: T.inkSoft, marginTop: 6, fontWeight: 600,
        }}>{subtitle}</div>
      </div>

      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
        padding: '16px 0',
      }}>
        <button
          type="button"
          onClick={() => speak(exercise.speakText)}
          style={{
            appearance: 'none', border: 'none', cursor: 'pointer',
            width: 110, height: 110, borderRadius: '50%',
            background: T.primary, boxShadow: `0 6px 0 ${T.primaryDeep}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {Icons.speaker('#fff')}
        </button>
        <div style={{
          fontSize: 12, fontWeight: 800, color: T.primary,
          letterSpacing: '0.06em',
        }}>{tapToHear}</div>
        {isSentence && (
          <button
            type="button"
            onClick={() => speak(exercise.speakText, { rate: 0.6 })}
            style={{
              appearance: 'none', cursor: 'pointer',
              background: T.bgAlt, border: `2px solid ${T.hairline}`,
              boxShadow: `0 3px 0 ${T.hairline}`,
              borderRadius: 999, padding: '8px 16px',
              display: 'flex', alignItems: 'center', gap: 8,
              fontFamily: 'inherit', fontWeight: 800, fontSize: 14,
              color: T.inkSoft, marginTop: 4,
            }}
          >
            <span aria-hidden="true">🐢</span>
            {t('listenSlow', lang)}
          </button>
        )}
      </div>

      {!hasCroatianVoice() && (
        <div style={{
          background: '#FFF1E6', color: T.inkSoft, padding: '10px 12px',
          borderRadius: 10, fontSize: 13, textAlign: 'center',
        }}>
          {lang === 'en'
            ? 'No Croatian voice in this browser — see the hint below.'
            : 'Ingen kroatisk röst i webbläsaren — använd ledtråden nedan.'}
        </div>
      )}

      {isSentence ? (
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={locked}
          rows={2}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder={placeholderWord}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          style={{ ...inputCommonStyle, resize: 'none' }}
        />
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={locked}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder={placeholderWord}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          style={inputCommonStyle}
        />
      )}

      <button
        type="button"
        onClick={() => setShowHint((v) => !v)}
        disabled={locked}
        style={{
          appearance: 'none', background: 'none', border: 'none', cursor: 'pointer',
          color: T.inkSoft, fontWeight: 800, fontSize: 13, letterSpacing: '0.04em',
          textTransform: 'uppercase', textDecoration: 'underline', padding: 8,
        }}
      >
        {showHint
          ? `${lang === 'en' ? 'Means' : 'Betyder'}: ${exercise.hint}`
          : (lang === 'en' ? 'Show meaning' : 'Visa översättning')}
      </button>
    </div>
  );
}
